import React, { useEffect, useRef, useState } from "react";
import { PlatformBrandIcon } from "./PlatformBrandIcon";

const PERIOD_OPTIONS = [
  { id: "today", label: "Today" },
  { id: "weekly", label: "Weekly" },
  { id: "monthly", label: "Monthly" },
  { id: "overall", label: "Overall" },
];

export function UsageSparkline({ series = [], color = "#2563eb", height = 36, variant = "line" }) {
  const data = (series || []).map((n) => Number(n) || 0);
  if (data.length < 2) {
    return <div className="ub-spark ub-spark--empty" style={{ height }} aria-hidden />;
  }
  const max = Math.max(...data, 1);
  const w = 100;
  const h = height;
  const step = w / (data.length - 1);
  if (variant === "bar") {
    const barW = Math.max(3, w / data.length - 2);
    return (
      <svg className="ub-spark ub-spark--bar" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden>
        {data.map((v, i) => {
          const bh = (v / max) * (h - 4);
          return (
            <rect
              key={i}
              x={i * (w / data.length) + 1}
              y={h - bh}
              width={barW}
              height={Math.max(2, bh)}
              rx={1.5}
              fill={color}
              opacity={0.85}
            />
          );
        })}
      </svg>
    );
  }
  const points = data
    .map((v, i) => {
      const x = i * step;
      const y = h - 4 - (v / max) * (h - 8);
      return `${x},${y}`;
    })
    .join(" ");
  return (
    <svg className="ub-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden>
      <polyline fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" points={points} />
    </svg>
  );
}

export function UsagePeriodChips({ value, onChange, className = "" }) {
  return (
    <div className={`ub-period-chips ${className}`.trim()} role="group" aria-label="Usage period">
      {PERIOD_OPTIONS.map((t) => (
        <button
          key={t.id}
          type="button"
          className={`ub-period-chip${value === t.id ? " is-active" : ""}`}
          aria-pressed={value === t.id}
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function UsagePageHeader({
  dateRangeLabel,
  usageRange,
  setUsageRange,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  calendarIcon,
}) {
  const overall = usageRange === "overall";
  const [dateOpen, setDateOpen] = useState(false);
  const fromRef = useRef(null);

  useEffect(() => {
    if (usageRange === "overall") setDateOpen(false);
  }, [usageRange]);

  useEffect(() => {
    if (!dateOpen) return;
    const el = fromRef.current;
    if (!el) return;
    try {
      el.focus({ preventScroll: true });
      if (typeof el.showPicker === "function") el.showPicker();
    } catch {
      /* showPicker requires a user gesture in some browsers */
    }
  }, [dateOpen]);

  const toggleDatePicker = () => {
    setDateOpen((open) => !open);
  };

  const showDateInputs = dateOpen;
  const inputsDisabled = overall && !dateOpen;
  const rangeSummary = dateRangeLabel || (overall ? "All time" : "Pick dates");

  return (
    <header className="ub-page-header adm-card">
      <div className="ub-page-header-main">
        <div className="ub-page-header-titles">
          <div className="ub-page-title-row">
            <h1 className="ub-page-title">Usage &amp; Billing</h1>
            <button
              type="button"
              className={`ub-page-date-trigger${dateOpen ? " is-open" : ""}`}
              onClick={toggleDatePicker}
              aria-expanded={dateOpen}
              aria-controls="ub-usage-date-range-panel"
              title={dateOpen ? "Hide date range" : `Date range: ${rangeSummary}`}
            >
              {calendarIcon ? (
                <span className="ub-date-cal-icon" aria-hidden>
                  {calendarIcon}
                </span>
              ) : null}
              {!dateOpen ? (
                <span className="ub-page-date-summary">{rangeSummary}</span>
              ) : (
                <span className="ub-page-date-summary ub-page-date-summary--open">From – To</span>
              )}
            </button>
          </div>
          <p className="ub-page-subtitle">
            Monitor MeetAI session minutes, billing usage, and operational health.
          </p>
        </div>
        <div className="ub-page-header-toolbar">
          {showDateInputs ? (
            <div
              id="ub-usage-date-range-panel"
              className="ub-usage-date-range"
              title={rangeSummary}
            >
              <label className="ub-usage-date-field">
                <span className="ub-sr-only">From date</span>
                <input
                  ref={fromRef}
                  type="date"
                  className="ub-usage-date-input"
                  value={overall && !dateOpen ? "" : dateFrom || ""}
                  disabled={inputsDisabled}
                  onChange={(e) => onDateFromChange(e.target.value)}
                  aria-label="From date"
                />
              </label>
              <span className="ub-usage-date-sep" aria-hidden>
                –
              </span>
              <label className="ub-usage-date-field">
                <span className="ub-sr-only">To date</span>
                <input
                  type="date"
                  className="ub-usage-date-input"
                  value={overall && !dateOpen ? "" : dateTo || dateFrom || ""}
                  disabled={inputsDisabled}
                  min={dateFrom || undefined}
                  onChange={(e) => onDateToChange(e.target.value)}
                  aria-label="To date"
                />
              </label>
            </div>
          ) : null}
          <UsagePeriodChips
            value={usageRange === "custom" ? "" : usageRange}
            onChange={setUsageRange}
            className="ub-page-header-periods"
          />
        </div>
      </div>
    </header>
  );
}

export function UsageChartSeriesToggles({ showMeetings, showCost, showTranscript, showBotHours, onToggle }) {
  const items = [
    { id: "meetings", label: "Meetings", active: showMeetings, color: "#2563EB" },
    { id: "cost", label: "Billed Cost", active: showCost, color: "#0f9d8f" },
    { id: "botHours", label: "Bot Hours", active: showBotHours, color: "#a855f7" },
    { id: "transcript", label: "Transcript Lines", active: showTranscript, color: "#ea580c" },
  ];
  return (
    <div className="ub-chart-series-toggles" role="group" aria-label="Chart series">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          className={`ub-chart-series-toggle${item.active ? " is-on" : ""}`}
          aria-pressed={item.active}
          onClick={() => onToggle(item.id)}
        >
          <span className="ub-legend-dot" style={{ background: item.color, opacity: item.active ? 1 : 0.35 }} />
          {item.label}
        </button>
      ))}
    </div>
  );
}

function CardIconButton({ label, title, onClick, children }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={title || label}
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 30,
        height: 30,
        borderRadius: 8,
        border: "1px solid #DCE6EF",
        background: "#FFFFFF",
        color: "#0875E1",
        cursor: "pointer",
        flexShrink: 0,
        padding: 0,
        appearance: "none",
        WebkitAppearance: "none",
      }}
    >
      {children}
    </button>
  );
}

function ThresholdBellIcon({ size = 13, color = "#0875E1" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 01-3.4 0" />
    </svg>
  );
}

export function UsageBillingAlertStatusCard({
  healthStatusLabel,
  healthStatusColor,
  loading,
  recallMcpLabel,
  recallMcpConnected,
  billingApiLabel,
  billingApiOk,
  thresholdRows,
  creditUsd,
  onEditThresholds,
  onOpenRecipients,
  onOpenHistory,
  recipientsIcon,
  historyIcon,
  recipientsCount,
}) {
  const shortHealth =
    healthStatusLabel && /healthy/i.test(healthStatusLabel) ? "Healthy" : healthStatusLabel || "—";
  return (
    <div className="adm-card ub-billing-alert-card ub-billing-health-sibling" id="ub-credit-panel">
      <div className="ub-billing-alert-head">
        <div className="ub-billing-alert-title">Billing &amp; Alert Status</div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <CardIconButton
            label={`Alert recipients${recipientsCount != null ? ` (${recipientsCount})` : ""}`}
            title={`Alert email recipients${recipientsCount != null ? ` (${recipientsCount})` : ""} — open to view and add`}
            onClick={onOpenRecipients}
          >
            {recipientsIcon}
          </CardIconButton>
          <CardIconButton
            label="Alert mail history"
            title="Alert mail history — when and what was sent"
            onClick={onOpenHistory}
          >
            {historyIcon}
          </CardIconButton>
          <span
            className="ub-credit-healthy-pill"
            style={{
              color: healthStatusColor,
              borderColor: `${healthStatusColor}44`,
              background: `${healthStatusColor}14`,
            }}
          >
            <span className="ub-credit-status-dot" style={{ background: healthStatusColor }} aria-hidden />
            {loading ? "…" : shortHealth}
          </span>
        </div>
      </div>
      <div className="ub-billing-alert-rows">
        <div className="ub-billing-alert-row">
          <span className="ub-billing-alert-label">Recall MCP</span>
          <StatusBadge ok={recallMcpConnected} label={recallMcpLabel} />
        </div>
        <div className="ub-billing-alert-row">
          <span className="ub-billing-alert-label">Billing API</span>
          <StatusBadge ok={billingApiOk} label={billingApiLabel} />
        </div>
      </div>
      <div
        className="ub-billing-alert-thresholds"
        id="ub-alert-thresholds"
        style={{ borderTop: "1px solid #EDF2F7", marginTop: 10, paddingTop: 10 }}
      >
        <div className="ub-credit-thresholds-head">
          <span
            className="ub-alert-thresholds-title"
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <ThresholdBellIcon size={13} color="#0875E1" />
            Alert Thresholds
          </span>
          {onEditThresholds ? (
            <button
              type="button"
              className="ub-btn-secondary ub-btn-secondary--sm"
              onClick={onEditThresholds}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 32,
                padding: "0 12px",
                borderRadius: 8,
                border: "1px solid #DCE6EF",
                background: "#FFFFFF",
                color: "#0875E1",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "inherit",
                appearance: "none",
                WebkitAppearance: "none",
              }}
            >
              Edit Thresholds
            </button>
          ) : null}
        </div>
        <div className="ub-credit-threshold-list ub-credit-threshold-list--compact">
          {(thresholdRows || [20, 15, 10, 5, 2].map((amount) => ({ amount }))).map((row) => {
            const amount = row.amount;
            const breached = creditUsd != null && creditUsd <= amount;
            const emailedAt = row.emailedAt;
            const dot = breached ? (amount <= 10 ? "#b0403f" : "#c98a2c") : "#0f9d8f";
            let stateLabel = "OK";
            if (creditUsd == null) stateLabel = "Unknown";
            else if (emailedAt) stateLabel = "Emailed";
            else if (breached) stateLabel = "Alert";
            return (
              <div key={amount} className="ub-threshold-row">
                <span className="ub-threshold-dot" style={{ background: dot }} />
                <span className="ub-threshold-val">${amount}</span>
                <span
                  className="ub-threshold-state"
                  style={{ color: stateLabel === "OK" ? "#64748b" : dot }}
                  title={emailedAt ? `Sent ${new Date(emailedAt).toLocaleString()}` : undefined}
                >
                  {stateLabel === "OK" ? "✓ OK" : stateLabel}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function UsageKpiCard({
  accent = "blue",
  icon,
  title,
  value,
  sublabel,
  trend,
  sparkSeries,
  sparkColor,
  sparkVariant = "line",
  onClick,
  onHistoryClick,
  historyIcon,
  titleAttr,
  showSpark = false,
}) {
  const historyBtn =
    onHistoryClick && historyIcon ? (
      <button
        type="button"
        className="ub-kpi-history-btn"
        aria-label="Credit balance history"
        title="Credit balance history"
        onClick={(e) => {
          e.stopPropagation();
          onHistoryClick();
        }}
      >
        {historyIcon}
      </button>
    ) : null;

  const inner = (
    <>
      <div className="ub-kpi-top">
        <span className={`ub-kpi-icon ub-kpi-icon--${accent}`}>{icon}</span>
        {historyBtn}
      </div>
      <div className="ub-kpi-title-row">
        <div className="ub-kpi-title">{title}</div>
        {trend ? (
          <span
            className={`ub-kpi-trend-badge${
              typeof trend === "object" && trend != null && Number(trend.pct) < 0 ? " is-down" : ""
            }`}
            title={typeof trend === "object" && trend != null ? trend.title || trend.short : trend}
          >
            {typeof trend === "object" && trend != null ? trend.short : trend}
          </span>
        ) : null}
      </div>
      <div className="ub-kpi-value">{value}</div>
      {sublabel ? <div className="ub-kpi-sub">{sublabel}</div> : null}
      {showSpark && sparkSeries && sparkSeries.length > 1 ? (
        <div className="ub-kpi-spark-wrap">
          <UsageSparkline series={sparkSeries} color={sparkColor} variant={sparkVariant} />
        </div>
      ) : null}
    </>
  );
  const cardClass = `ub-kpi-card ub-kpi-card--${accent}${onClick ? " ub-kpi-card--interactive" : ""}`;
  if (onClick && !onHistoryClick) {
    return (
      <button type="button" className={cardClass} onClick={onClick} title={titleAttr}>
        {inner}
      </button>
    );
  }
  if (onClick) {
    return (
      <div
        className={cardClass}
        role="button"
        tabIndex={0}
        title={titleAttr}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        }}
      >
        {inner}
      </div>
    );
  }
  return (
    <div className={cardClass} title={titleAttr}>
      {inner}
    </div>
  );
}

export function UsageCreditBalanceCard({
  balanceUsd,
  statusLabel,
  statusColor,
  progressPct,
  loading,
  onOpenHistory,
  healthBlurb,
}) {
  const shortStatus =
    statusLabel && /healthy/i.test(statusLabel) ? "Healthy" : statusLabel || "—";
  return (
    <div className="adm-card ub-credit-balance-card">
      <div className="ub-credit-balance-head">
        <div className="ub-credit-balance-card-title">Credit balance status</div>
        <span className="ub-credit-healthy-pill" style={{ color: statusColor, borderColor: `${statusColor}44`, background: `${statusColor}14` }}>
          <span className="ub-credit-status-dot" style={{ background: statusColor }} aria-hidden />
          {loading ? "…" : shortStatus}
        </span>
      </div>
      {healthBlurb ? <p className="ub-credit-blurb">{healthBlurb}</p> : null}
      <div className="ub-credit-balance-hero">
        <span className="ub-credit-balance-label">Remaining credits</span>
        <span className="ub-credit-balance-amount">{loading ? "…" : balanceUsd == null ? "—" : balanceUsd}</span>
      </div>
      <div className="ub-credit-progress-row">
        <div className="ub-credit-progress-track" aria-hidden>
          <div
            className="ub-credit-progress-fill"
            style={{ width: `${Math.min(100, Math.max(0, progressPct))}%`, background: statusColor }}
          />
        </div>
        <span className="ub-credit-progress-pct">{loading ? "—" : `${Math.round(progressPct)}%`}</span>
      </div>
      {onOpenHistory ? (
        <button type="button" className="ub-link-btn ub-credit-history-link" onClick={onOpenHistory}>
          Credit history
        </button>
      ) : null}
    </div>
  );
}

export function UsageAlertThresholdsCard({ thresholdRows, creditUsd, onEditThresholds }) {
  return (
    <div className="adm-card ub-alert-thresholds-card" id="ub-alert-thresholds">
      <div className="ub-credit-thresholds-head">
        <span className="ub-alert-thresholds-title">Alert thresholds</span>
        {onEditThresholds ? (
          <button type="button" className="ub-link-btn" onClick={onEditThresholds}>
            Edit thresholds
          </button>
        ) : null}
      </div>
      <div className="ub-credit-threshold-list">
        {(thresholdRows || [20, 15, 10, 5, 2].map((amount) => ({ amount }))).map((row) => {
          const amount = row.amount;
          const breached = creditUsd != null && creditUsd <= amount;
          const emailedAt = row.emailedAt;
          const dot = breached ? (amount <= 10 ? "#b0403f" : "#c98a2c") : "#0f9d8f";
          let stateLabel = "OK";
          if (creditUsd == null) stateLabel = "Unknown";
          else if (emailedAt) stateLabel = "Emailed";
          else if (breached) stateLabel = "Alert";
          return (
            <div key={amount} className="ub-threshold-row">
              <span className="ub-threshold-dot" style={{ background: dot }} />
              <span className="ub-threshold-val">${amount}</span>
              <span
                className="ub-threshold-state"
                style={{ color: stateLabel === "OK" ? "#64748b" : dot }}
                title={emailedAt ? `Sent ${new Date(emailedAt).toLocaleString()}` : undefined}
              >
                {stateLabel === "OK" ? "OK" : stateLabel}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** @deprecated use UsageCreditBalanceCard + UsageAlertThresholdsCard */
export function UsageCreditHealthPanel(props) {
  return (
    <div className="ub-trend-side-stack">
      <UsageCreditBalanceCard {...props} healthBlurb="Credit balance from Recall MCP get_billed_usage." />
      <UsageAlertThresholdsCard
        thresholdRows={props.thresholdRows}
        creditUsd={props.creditUsd}
        onEditThresholds={props.onEditThresholds}
      />
    </div>
  );
}

const PLATFORM_STYLE = {
  meet: { bar: "#34a853", bg: "rgba(52,168,83,0.12)", label: "Meet" },
  google_meet: { bar: "#34a853", bg: "rgba(52,168,83,0.12)", label: "Meet" },
  zoom: { bar: "#2d8cff", bg: "rgba(45,140,255,0.12)", label: "Zoom" },
  teams: { bar: "#5b5fc7", bg: "rgba(91,95,199,0.12)", label: "Teams" },
};

export function UsagePlatformBars({ byPlatform, renderPlatformIcon, emptyLabel = "No usage data yet." }) {
  const entries = Object.entries(byPlatform || {}).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((s, [, c]) => s + c, 0) || 0;
  if (!entries.length) {
    return <div className="ub-empty">{emptyLabel}</div>;
  }
  const styleFor = (key) => {
    const k = String(key || "").toLowerCase();
    if (k.includes("zoom")) return PLATFORM_STYLE.zoom;
    if (k.includes("team")) return PLATFORM_STYLE.teams;
    return PLATFORM_STYLE.meet;
  };
  const labelFor = (key) => styleFor(key).label;
  return (
    <div className="ub-platform-bars">
      {entries.map(([platform, count]) => {
        const pct = total > 0 ? (count / total) * 100 : 0;
        const st = styleFor(platform);
        return (
          <div key={platform} className="ub-platform-row ub-platform-row--compact">
            <div className="ub-platform-row-top">
              <span className="ub-platform-name-wrap">
                <span className="ub-platform-icon" style={{ background: st.bg }}>
                  {renderPlatformIcon ? (
                    renderPlatformIcon(platform)
                  ) : (
                    <PlatformBrandIcon platform={platform} size={14} />
                  )}
                </span>
                <span className="ub-platform-name">{labelFor(platform)}</span>
              </span>
              <span className="ub-platform-count">{count.toLocaleString()}</span>
              <span className="ub-platform-pct">{pct.toFixed(1)}%</span>
            </div>
            <div className="ub-platform-bar-track ub-platform-bar-track--compact">
              <div className="ub-platform-bar-fill" style={{ width: `${Math.max(pct, 4)}%`, background: st.bar }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function UsageStatusDonut({ completed, live, size = "sm", active, onClick, className = "" }) {
  const c = Math.max(0, Number(completed) || 0);
  const l = Math.max(0, Number(live) || 0);
  const total = c + l || 1;
  const pct = (c / total) * 100;
  const ringClass = size === "md" ? "ub-status-ring--md" : "ub-status-ring--sm";
  const holeClass = size === "md" ? "ub-status-ring-hole--md" : "ub-status-ring-hole--sm";
  return (
    <button
      type="button"
      className={`ub-status-ring-btn${active ? " is-active" : ""} ${className}`.trim()}
      onClick={onClick}
      aria-pressed={active}
      aria-label={`${c.toLocaleString()} completed, ${l.toLocaleString()} live`}
    >
      <div
        className={`ub-status-ring ${ringClass}`}
        style={{
          background: `conic-gradient(#0f9d8f 0 ${pct}%, #e2e8f0 ${pct}% 100%)`,
        }}
        aria-hidden
      >
        <div className={`ub-status-ring-hole ${holeClass}`}>
          <span className="ub-status-ring-center-num">{c.toLocaleString()}</span>
          <span className="ub-status-ring-center-label">Completed</span>
        </div>
      </div>
    </button>
  );
}

function UsageStatusStatLines({
  completed,
  live,
  completedActive,
  liveActive,
  onCompletedClick,
  onLiveClick,
}) {
  const c = Math.max(0, Number(completed) || 0);
  const l = Math.max(0, Number(live) || 0);
  return (
    <div className="ub-status-stats-col">
      <button
        type="button"
        className={`ub-status-stat-line ub-status-stat-line--row${completedActive ? " is-active" : ""}`}
        onClick={onCompletedClick}
      >
        <span className="ub-status-stat-dot ub-status-stat-dot--completed" aria-hidden />
        <span className="ub-status-stat-label">Completed</span>
        <span className="ub-status-stat-num ub-status-stat-num--inline">{c.toLocaleString()}</span>
      </button>
      <button
        type="button"
        className={`ub-status-stat-line ub-status-stat-line--row${liveActive ? " is-active" : ""}`}
        onClick={onLiveClick}
      >
        <span className="ub-status-stat-dot ub-status-stat-dot--live" aria-hidden />
        <span className="ub-status-stat-label">Live</span>
        <span className="ub-status-stat-num ub-status-stat-num--inline">{l.toLocaleString()}</span>
      </button>
    </div>
  );
}

export function UsageStatusRing({
  completed,
  live,
  onCompletedClick,
  onLiveClick,
  completedActive,
  liveActive,
  showDonut = true,
  showStats = true,
}) {
  return (
    <div className="ub-status-ring-layout ub-status-ring-layout--compact">
      {showDonut ? (
        <UsageStatusDonut
          completed={completed}
          live={live}
          active={completedActive}
          onClick={onCompletedClick}
        />
      ) : null}
      {showStats ? (
        <UsageStatusStatLines
          completed={completed}
          live={live}
          completedActive={completedActive}
          liveActive={liveActive}
          onCompletedClick={onCompletedClick}
          onLiveClick={onLiveClick}
        />
      ) : null}
    </div>
  );
}

function HealthChip({ label, children, title }) {
  const tip = title || (typeof children === "string" ? children : undefined);
  return (
    <div className="ub-system-health-chip" title={tip}>
      <span className="ub-system-health-chip-label">{label}</span>
      <div className="ub-system-health-chip-value">{children}</div>
    </div>
  );
}

export function UsageSystemHealth({
  recallConnected,
  recallLabel,
  region,
  pricingBot,
  pricingTranscription,
  billingApiLabel,
  billingApiOk,
  getBilledUsageStatus,
  retryLabel,
  lastUpdated,
  cacheLabel,
  workspaceLabel,
  layout = "grid",
}) {
  const billedOk = getBilledUsageStatus === "OK";
  const pricingTitle = [pricingBot, pricingTranscription].filter(Boolean).join(" · ");

  if (layout === "horizontal") {
    const pricingOneLine =
      [pricingBot, pricingTranscription].filter(Boolean).join(" · ") || "—";
    return (
      <div className="adm-card ub-system-health ub-system-health--compact ub-system-health--horizontal ub-billing-health-sibling">
        <div className="ub-system-health-title">System Health</div>
        <div className="ub-system-health-wrap" role="list" aria-label="System health details">
          <HealthChip label="Recall MCP" title={recallLabel}>
            <StatusBadge ok={recallConnected} label={recallLabel} />
          </HealthChip>
          <HealthChip label="Region" title={region}>
            {region || "—"}
          </HealthChip>
          <HealthChip label="Pricing" title={pricingTitle}>
            {pricingOneLine}
          </HealthChip>
          <HealthChip label="Billing API" title={billingApiLabel}>
            <StatusBadge ok={billingApiOk} label={billingApiLabel} />
          </HealthChip>
          <HealthChip label="Billed usage" title={getBilledUsageStatus}>
            <StatusBadge ok={billedOk} label={getBilledUsageStatus} degraded={!billedOk && getBilledUsageStatus !== "DOWN"} />
          </HealthChip>
          <HealthChip label="Retry" title={retryLabel}>
            {retryLabel || "—"}
          </HealthChip>
          <HealthChip label="Updated" title={lastUpdated}>
            {lastUpdated || "—"}
          </HealthChip>
          {cacheLabel ? (
            <HealthChip label="Cache" title={cacheLabel}>
              {cacheLabel}
            </HealthChip>
          ) : null}
          {workspaceLabel ? (
            <HealthChip label="Workspace" title={workspaceLabel}>
              {workspaceLabel}
            </HealthChip>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="adm-card ub-system-health ub-system-health--compact">
      <div className="ub-system-health-title">System Health</div>
      <div className="ub-system-health-grid ub-system-health-grid--2col">
        <div className="ub-health-cell">
          <span className="ub-health-label">Recall MCP</span>
          <StatusBadge ok={recallConnected} label={recallLabel} />
        </div>
        <div className="ub-health-cell">
          <span className="ub-health-label">Region</span>
          <span className="ub-health-value">{region}</span>
        </div>
        <div className="ub-health-cell ub-health-cell--span2">
          <span className="ub-health-label">Pricing</span>
          <span className="ub-health-value ub-health-value--stack">
            {pricingBot ? <span>{pricingBot}</span> : null}
            {pricingTranscription ? <span>{pricingTranscription}</span> : null}
          </span>
        </div>
        <div className="ub-health-cell">
          <span className="ub-health-label">Billing API</span>
          <StatusBadge ok={billingApiOk} label={billingApiLabel} />
        </div>
        <div className="ub-health-cell">
          <span className="ub-health-label">get_billed_usage</span>
          <StatusBadge ok={billedOk} label={getBilledUsageStatus} degraded={!billedOk && getBilledUsageStatus !== "DOWN"} />
        </div>
        <div className="ub-health-cell">
          <span className="ub-health-label">Auto retry</span>
          <span className="ub-health-value">{retryLabel}</span>
        </div>
        <div className="ub-health-cell">
          <span className="ub-health-label">Last updated</span>
          <span className="ub-health-value">{lastUpdated}</span>
        </div>
        {cacheLabel ? (
          <div className="ub-health-cell ub-health-cell--span2">
            <span className="ub-health-label">Backend cache</span>
            <span className="ub-health-value">{cacheLabel}</span>
          </div>
        ) : null}
        {workspaceLabel ? (
          <div className="ub-health-cell">
            <span className="ub-health-label">Workspace</span>
            <span className="ub-health-value">{workspaceLabel}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function StatusBadge({ ok, label, color, degraded }) {
  let bg = "#ecfdf5";
  let fg = "#047857";
  if (color) {
    fg = color;
    bg = `${color}18`;
  } else if (degraded) {
    bg = "#fffbeb";
    fg = "#b45309";
  } else if (!ok) {
    bg = "#fef2f2";
    fg = "#b91c1c";
  }
  return (
    <span className="ub-status-badge" style={{ color: fg, background: bg, borderColor: `${fg}33` }}>
      <span className="ub-status-badge-dot" style={{ background: fg }} aria-hidden />
      {label}
    </span>
  );
}

export function UsageKpiSkeletonRow() {
  return (
    <div className="ub-kpi-row" aria-busy="true" aria-label="Loading metrics">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="ub-kpi-card ub-kpi-skeleton">
          <div className="ub-kpi-skeleton-icon" />
          <div className="ub-kpi-skeleton-line ub-kpi-skeleton-line--lg" />
          <div className="ub-kpi-skeleton-line" />
          <div className="ub-kpi-skeleton-line ub-kpi-skeleton-line--sm" />
        </div>
      ))}
    </div>
  );
}

export function UsagePlatformStatusCard({
  byPlatform,
  renderPlatformIcon,
  completed,
  live,
  completedActive,
  liveActive,
  onCompletedClick,
  onLiveClick,
  statusFilterHint,
  onViewDetails,
}) {
  return (
    <div className="adm-card ub-section-card ub-platform-status-card">
      <section className="ub-platform-status-pane" aria-labelledby="ub-platform-heading">
        <h3 id="ub-platform-heading" className="ub-platform-status-pane-title">
          Usage by Platform
        </h3>
        <UsagePlatformBars byPlatform={byPlatform} renderPlatformIcon={renderPlatformIcon} />
      </section>

      <section
        className="ub-platform-status-pane ub-platform-status-pane--status"
        id="ub-usage-status"
        aria-labelledby="ub-status-heading"
      >
        <div className="ub-platform-status-pane-head">
          <h3 id="ub-status-heading" className="ub-platform-status-pane-title">
            Usage by Status
          </h3>
          {onViewDetails ? (
            <button
              type="button"
              className="ub-header-text-btn ub-platform-status-details-btn"
              onClick={onViewDetails}
              title="View all meetings"
            >
              Details
            </button>
          ) : null}
        </div>
        {statusFilterHint ? <p className="ub-section-sub ub-platform-status-filter-hint">{statusFilterHint}</p> : null}
        <div className="ub-platform-status-donut-foot">
          <UsageStatusDonut
            completed={completed}
            live={live}
            size="md"
            active={completedActive}
            onClick={onCompletedClick}
          />
          <UsageStatusStatLines
            completed={completed}
            live={live}
            completedActive={completedActive}
            liveActive={liveActive}
            onCompletedClick={onCompletedClick}
            onLiveClick={onLiveClick}
          />
        </div>
      </section>
    </div>
  );
}

export function UsageSectionCard({ title, subtitle, actions, children, className = "", id }) {
  return (
    <div id={id} className={`adm-card ub-section-card ${className}`.trim()}>
      <div className="ub-section-head">
        <div className="ub-section-head-text">
          {title ? <div className="ub-section-title">{title}</div> : null}
          {subtitle ? <div className="ub-section-sub">{subtitle}</div> : null}
        </div>
        {actions ? <div className="ub-section-actions">{actions}</div> : null}
      </div>
      {children}
    </div>
  );
}
